// 0 = _
// 1 = ◢
// 2 = ◣
// 3 = ◥
// 4 = ◤

import { getRandomRulesSet, oneAutomataStep, state } from './automata'
import {
  AUTOMATON_CHANGE,
  COLOUR_EASING,
  HORIZONTAL_MARGIN_PERCENTAGE,
  SHAPE_MORPH_SPEED,
  SNAP_DISTANCE
} from './const'
import { awaitNewAnimationFrame, sleep } from './lib/anim'
import { speedRatio, stats } from './lib/fps'
import { mul2 } from './lib/math'
import { getSettings, paused } from './lib/settings'
import { viewport } from './lib/viewport'
import { getCurrentPalette } from './palette'
import { SHAPES_POINTS } from './shapes-points'
import {
  Cell,
  CellKind,
  CellState,
  DisplayState,
  GlobalState,
  HSL
} from './types'

// CellShape is now a Float32Array with flat layout: [x0, y0, x1, y1, x2, y2]
export type CellShape = Float32Array

let speedRatioCached = speedRatio()

// WebGL shader sources
const VERTEX_SHADER_SOURCE = `
  attribute vec2 a_position;
  attribute vec3 a_color;
  
  uniform vec2 u_resolution;
  
  varying vec3 v_color;
  
  void main() {
    // Convert pixel coordinates to clip space (-1 to 1)
    vec2 clipSpace = (a_position / u_resolution) * 2.0 - 1.0;
    // Flip Y axis (WebGL has Y going up, canvas has Y going down)
    gl_Position = vec4(clipSpace.x, -clipSpace.y, 0.0, 1.0);
    v_color = a_color;
  }
`

const FRAGMENT_SHADER_SOURCE = `
  precision mediump float;
  
  varying vec3 v_color;
  
  void main() {
    gl_FragColor = vec4(v_color, 1.0);
  }
`

// WebGL context and buffers
interface WebGLState {
  gl: WebGLRenderingContext
  program: WebGLProgram
  positionBuffer: WebGLBuffer
  colorBuffer: WebGLBuffer
  positionLocation: number
  colorLocation: number
  resolutionLocation: WebGLUniformLocation
}

let webglState: WebGLState | null = null

function createShader(
  gl: WebGLRenderingContext,
  type: number,
  source: string
): WebGLShader | null {
  const shader = gl.createShader(type)
  if (!shader) return null

  gl.shaderSource(shader, source)
  gl.compileShader(shader)

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    console.error('Shader compile error:', gl.getShaderInfoLog(shader))
    gl.deleteShader(shader)
    return null
  }

  return shader
}

function createProgram(
  gl: WebGLRenderingContext,
  vertexShader: WebGLShader,
  fragmentShader: WebGLShader
): WebGLProgram | null {
  const program = gl.createProgram()
  if (!program) return null

  gl.attachShader(program, vertexShader)
  gl.attachShader(program, fragmentShader)
  gl.linkProgram(program)

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(program))
    gl.deleteProgram(program)
    return null
  }

  return program
}

export function initWebGL(canvas: HTMLCanvasElement): WebGLState | null {
  const gl = canvas.getContext('webgl', {
    antialias: true,
    alpha: false,
    premultipliedAlpha: false
  })

  if (!gl) {
    console.error('WebGL not supported')
    return null
  }

  const vertexShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER_SOURCE)
  const fragmentShader = createShader(
    gl,
    gl.FRAGMENT_SHADER,
    FRAGMENT_SHADER_SOURCE
  )

  if (!vertexShader || !fragmentShader) return null

  const program = createProgram(gl, vertexShader, fragmentShader)
  if (!program) return null

  const positionBuffer = gl.createBuffer()
  const colorBuffer = gl.createBuffer()

  if (!positionBuffer || !colorBuffer) return null

  const positionLocation = gl.getAttribLocation(program, 'a_position')
  const colorLocation = gl.getAttribLocation(program, 'a_color')
  const resolutionLocation = gl.getUniformLocation(program, 'u_resolution')

  if (resolutionLocation === null) return null

  webglState = {
    gl,
    program,
    positionBuffer,
    colorBuffer,
    positionLocation,
    colorLocation,
    resolutionLocation
  }

  return webglState
}

export function getDimensions(): DisplayState {
  const vp = mul2(viewport(), window.devicePixelRatio)
  const horizontalMargin = (vp[0] * HORIZONTAL_MARGIN_PERCENTAGE) / 100

  const horizontalSpaceWithinMargins = vp[0] - horizontalMargin * 2
  const verticalSpaceWithinMargins = vp[1] - horizontalMargin * 2
  const withMarginsRatio =
    horizontalSpaceWithinMargins / verticalSpaceWithinMargins

  const settings = getSettings()
  const { columns } = settings
  console.log('columns', columns)

  const rows = Math.round(columns / withMarginsRatio)
  const cellSize = (vp[0] - horizontalMargin * 2) / columns
  const heightTakenUpByCells = rows * cellSize
  const verticalMargin = (vp[1] - heightTakenUpByCells) / 2
  const result = {
    verticalMargin,
    rows,
    horizontalMargin,
    cellSize,
    columns
  }
  return result
}

export function cellAt(
  grid: Cell[],
  x: number,
  y: number,
  columns: number,
  rows: number
): Cell {
  const cell = grid[x + y * columns]
  if (cell === undefined || y < 0 || x < 0 || x >= columns || y >= rows)
    return { kind: 0, group: null }
  return cell
}

export function createGrid(
  initValue: CellKind,
  columns: number,
  rows: number
): Cell[] {
  const newGrid: Cell[] = []

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < columns; x++) {
      const nextCell: Cell = {
        kind: initValue,
        group: null
      }
      newGrid[x + y * columns] = nextCell
    }
  }
  return addGroupNumbers(newGrid, rows, columns)
}

export function getTargetShape(kind: CellKind): CellShape {
  return new Float32Array(SHAPES_POINTS[kind])
}

export function updateAllCellsState(state: GlobalState): void {
  const { animation, grid } = state
  const len = animation.length
  const shapeMorphSpeed = (SHAPE_MORPH_SPEED / speedRatioCached) * 4
  for (let i = 0; i < len; i++) {
    updateCellState(grid[i], animation[i], shapeMorphSpeed)
  }
}

function shiftHue(color: HSL, shift: number): HSL {
  return [(color[0] + shift) % 360, color[1], color[2]]
}

function updateCellStateColour(cell: Cell, cellState: CellState): void {
  const targetColour = getCellColour(cell.group)
  const currentColour: HSL = cellState.color
  const mul = COLOUR_EASING * speedRatioCached
  const targetColourShifted = shiftHue(targetColour, cellState.age / 100)
  currentColour[0] += (targetColourShifted[0] - currentColour[0]) / mul
  currentColour[1] += (targetColourShifted[1] - currentColour[1]) / mul
  currentColour[2] += (targetColourShifted[2] - currentColour[2]) / mul
}

function updateShape(cellState: CellState, shapeMorphSpeed: number): void {
  const { currentShape, targetShape } = cellState

  // Iterate over 6 values: [x0, y0, x1, y1, x2, y2]
  for (let i = 0; i < 6; i++) {
    const current = currentShape[i]
    const target = targetShape[i]
    const distance = Math.abs(current - target)

    if (distance < SNAP_DISTANCE) {
      currentShape[i] = target
    } else if (target > current) {
      currentShape[i] += shapeMorphSpeed
    } else if (target < current) {
      currentShape[i] -= shapeMorphSpeed
    }
  }
}

function updateCellState(
  cell: Cell,
  cellState: CellState,
  cellMorphSpeed: number
): void {
  updateShape(cellState, cellMorphSpeed)
  updateCellStateColour(cell, cellState)
  cellState.age += 4 / speedRatioCached
}

// Convert HSL (all components 0-255) to RGB (0-1) for WebGL
// H: 0-255 maps to 0-360 degrees
// S: 0-255 maps to 0-100%
// L: 0-255 maps to 0-100%
function hslToRgb(color: HSL): [number, number, number] {
  const h = (color[0] / 255) * 360
  const s = color[1] / 255
  const l = color[2] / 255

  const c = (1 - Math.abs(2 * l - 1)) * s
  const hPrime = h / 60
  const x = c * (1 - Math.abs((hPrime % 2) - 1))
  const m = l - c / 2

  let r = 0,
    g = 0,
    b = 0

  if (hPrime >= 0 && hPrime < 1) {
    r = c
    g = x
    b = 0
  } else if (hPrime >= 1 && hPrime < 2) {
    r = x
    g = c
    b = 0
  } else if (hPrime >= 2 && hPrime < 3) {
    r = 0
    g = c
    b = x
  } else if (hPrime >= 3 && hPrime < 4) {
    r = 0
    g = x
    b = c
  } else if (hPrime >= 4 && hPrime < 5) {
    r = x
    g = 0
    b = c
  } else if (hPrime >= 5 && hPrime < 6) {
    r = c
    g = 0
    b = x
  }

  return [r + m, g + m, b + m]
}

// Reusable typed arrays for WebGL data
let positionData: Float32Array | null = null
let colorData: Float32Array | null = null

function ensureBufferSizes(cellCount: number): void {
  const positionSize = cellCount * 6 // 3 vertices * 2 coords per cell
  const colorSize = cellCount * 9 // 3 vertices * 3 color components per cell

  if (!positionData || positionData.length !== positionSize) {
    positionData = new Float32Array(positionSize)
  }
  if (!colorData || colorData.length !== colorSize) {
    colorData = new Float32Array(colorSize)
  }
}

export function drawCells(gl: WebGLRenderingContext, state: GlobalState): void {
  if (!webglState) return

  const { display, animation } = state
  const { columns } = state.display
  const len = state.animation.length
  const { cellSize, horizontalMargin, verticalMargin } = display

  ensureBufferSizes(len)
  if (!positionData || !colorData) return

  let posIndex = 0
  let colorIndex = 0

  for (let i = 0; i < len; i++) {
    const x = i % columns
    const y = (i / columns) | 0

    const cellState = animation[i]
    const cx = x * cellSize + horizontalMargin
    const cy = y * cellSize + verticalMargin

    const { currentShape, color } = cellState
    const [r, g, b] = hslToRgb(color)

    // Add triangle vertices from flat array [x0, y0, x1, y1, x2, y2]
    positionData[posIndex++] = currentShape[0] * cellSize + cx
    positionData[posIndex++] = currentShape[1] * cellSize + cy
    positionData[posIndex++] = currentShape[2] * cellSize + cx
    positionData[posIndex++] = currentShape[3] * cellSize + cy
    positionData[posIndex++] = currentShape[4] * cellSize + cx
    positionData[posIndex++] = currentShape[5] * cellSize + cy

    // Same color for all 3 vertices
    colorData[colorIndex++] = r
    colorData[colorIndex++] = g
    colorData[colorIndex++] = b
    colorData[colorIndex++] = r
    colorData[colorIndex++] = g
    colorData[colorIndex++] = b
    colorData[colorIndex++] = r
    colorData[colorIndex++] = g
    colorData[colorIndex++] = b
  }

  const {
    program,
    positionBuffer,
    colorBuffer,
    positionLocation,
    colorLocation,
    resolutionLocation
  } = webglState

  gl.useProgram(program)

  // Set resolution uniform
  gl.uniform2f(resolutionLocation, gl.canvas.width, gl.canvas.height)

  // Upload position data
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
  gl.bufferData(gl.ARRAY_BUFFER, positionData, gl.DYNAMIC_DRAW)
  gl.enableVertexAttribArray(positionLocation)
  gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)

  // Upload color data
  gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer)
  gl.bufferData(gl.ARRAY_BUFFER, colorData, gl.DYNAMIC_DRAW)
  gl.enableVertexAttribArray(colorLocation)
  gl.vertexAttribPointer(colorLocation, 3, gl.FLOAT, false, 0, 0)

  // Draw all triangles
  gl.drawArrays(gl.TRIANGLES, 0, len * 3)
}

function getCellColour(cellGroup: number | null): HSL {
  if (cellGroup === null) return [200, 200, 200]
  const currentPalette = getCurrentPalette()
  return currentPalette[cellGroup % currentPalette.length]
}

export function addGroupNumbers(
  grid: Cell[],
  rows: number,
  columns: number
): Cell[] {
  const newGrid = cloneGrid(grid)
  newGrid.forEach((cell) => {
    cell.group = null
  })
  const len = newGrid.length
  for (let i = 0; i < len; i++) {
    const x = i % columns
    const y = (i / columns) << 0
    traverseCellToGiveGroup(newGrid, x, y, i, columns, rows)
  }
  return newGrid
}

export function cloneGrid(cells: Cell[]): Cell[] {
  const result: Cell[] = []
  const len = cells.length
  for (let i = 0; i < len; i++) {
    result[i] = { ...cells[i] }
  }
  return result
}

function changeRandomCell(grid: Cell[]): Cell[] {
  const gridCopy = cloneGrid(grid)
  const randomIndex = Math.round(Math.random() * (gridCopy.length - 1))
  const newKind = (Math.round(Math.random() * 3) + 1) as CellKind
  gridCopy[randomIndex] = {
    group: null,
    kind: newKind
  }
  return gridCopy
}

export function updateCellsOneStep(globalState: GlobalState): void {
  globalState.grid = changeRandomCell(globalState.grid)
  const result = oneAutomataStep(globalState)

  if (result.changedCellKinds < 10) {
    state.rules = getRandomRulesSet()
  }

  globalState.grid = result.output
  globalState.grid = addGroupNumbers(
    globalState.grid,
    globalState.display.rows,
    globalState.display.columns
  )

  updateDestinationShapes(globalState)
}

function updateDestinationShapes(globalState: GlobalState): void {
  const { animation, grid } = globalState
  const len = grid.length
  for (let i = 0; i < len; i++) {
    const cell = grid[i]
    const cellState = animation[i]
    cellState.targetShape = getTargetShape(cell.kind)
  }
}

export function setCanvasDimensions(canvas: HTMLCanvasElement): void {
  const vp = viewport()
  canvas.width = vp[0] * window.devicePixelRatio
  canvas.height = vp[1] * window.devicePixelRatio
  canvas.style.width = `${vp[0]}px`
  canvas.style.height = `${vp[1]}px`
  canvas.style.position = 'fixed'
  canvas.style.left = '0px'
  canvas.style.top = '0px'

  // Update WebGL viewport if initialized
  if (webglState) {
    webglState.gl.viewport(0, 0, canvas.width, canvas.height)
  }
}

export function draw(gl: WebGLRenderingContext, state: GlobalState): void {
  updateAllCellsState(state)
  drawCells(gl, state)
}

export async function theLoop(
  canvas: HTMLCanvasElement,
  gl: WebGLRenderingContext,
  state: GlobalState
): Promise<void> {
  let automatonStepCounter = 0

  // Initialize WebGL if not already done
  if (!webglState) {
    const initialized = initWebGL(canvas)
    if (!initialized) {
      console.error('Failed to initialize WebGL')
      return
    }
  }

  while (true) {
    stats.begin()

    // Clear with gray background
    gl.clearColor(0.5, 0.5, 0.5, 1.0)
    gl.clear(gl.COLOR_BUFFER_BIT)

    speedRatioCached = speedRatio()
    draw(gl, state)

    if (automatonStepCounter > AUTOMATON_CHANGE) {
      updateCellsOneStep(state)
      automatonStepCounter = 0
    } else {
      automatonStepCounter += 4 / speedRatioCached
    }

    await awaitNewAnimationFrame()
    while (paused) {
      await sleep(100)
    }
    stats.end()
  }
}

export function traverseCellToGiveGroup(
  grid: Cell[],
  x: number,
  y: number,
  newGroupNumber: number,
  columns: number,
  rows: number
): void {
  const cell = cellAt(grid, x, y, columns, rows)
  if (x < 0 || x > columns || y < 0 || y > rows || cell.group !== null) {
    return
  }

  cell.group = newGroupNumber

  function check(dx: number, dy: number, kind: CellKind) {
    if (cellAt(grid, x + dx, y + dy, columns, rows).kind === kind) {
      traverseCellToGiveGroup(
        grid,
        x + dx,
        y + dy,
        newGroupNumber,
        columns,
        rows
      )
    }
  }

  switch (cell.kind) {
    case 1: // ◢
      check(1, 0, 2)
      check(1, 0, 4)
      check(0, 1, 3)
      check(0, 1, 4)
      break
    case 2: // ◣
      check(-1, 1, 1)
      check(-1, 1, 3)
      check(0, 1, 4)
      check(0, 1, 3)
      check(-1, 0, 3)
      check(-1, 0, 1)
      break
    case 3: // ◥
      check(1, 0, 4)
      check(1, 0, 2)
      check(0, -1, 2)
      check(0, -1, 1)
      break
    case 4: // ◤
      check(0, -1, 2)
      check(0, -1, 1)
      check(-1, 0, 1)
      check(-1, 0, 3)
      break
  }
}

// Export for external initialization
export function getWebGLContext(
  canvas: HTMLCanvasElement
): WebGLRenderingContext | null {
  if (webglState) {
    return webglState.gl
  }
  const initialized = initWebGL(canvas)
  return initialized ? initialized.gl : null
}
